import { AlertCircle, ChevronRight } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const items = [
  "A consulta durou 15 minutos e você saiu com uma receita, sem entender o porquê",
  "Cada retorno foi com um médico diferente, e você teve que recontar tudo do zero",
  "O ajuste da medicação nunca aconteceu, porque nunca houve tempo para revisar",
  "Você tem dúvida sobre um efeito colateral e não sabe a quem perguntar",
  "Você adiou o retorno porque não sentiu que ele mudaria alguma coisa",
];

function RevealItem({ text, delayMs, index }: { text: string; delayMs: number; index: number }) {
  const { ref, visible } = useScrollReveal<HTMLLIElement>();
  return (
    <li
      ref={ref}
      className={
        "reveal " +
        (visible ? "reveal-visible " : "") +
        "spotlight group relative flex items-start gap-4 rounded-2xl border border-[#E5E3DE] bg-white/85 backdrop-blur p-5 md:p-6 card-lift card-lift-hover hover:border-[#17724F]/45"
      }
      style={{ transitionDelay: `${delayMs}ms` }}
    >
      <span className="spotlight-layer" aria-hidden="true" />
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#0F2A47]/[0.06] text-[#0F2A47] group-hover:bg-[#17724F] group-hover:text-white transition-all">
        <span className="font-serif text-[15px] font-semibold tabular-nums">
          {String(index + 1).padStart(2, "0")}
        </span>
      </div>
      <p className="flex-1 text-[16px] md:text-[17.5px] text-[#2A2E33] leading-[1.6]">{text}</p>
      <ChevronRight
        size={18}
        className="mt-1 shrink-0 text-[#17724F]/0 group-hover:text-[#17724F] transition-all group-hover:translate-x-1"
        aria-hidden="true"
      />
    </li>
  );
}

export function IdentificacaoSection() {
  return (
    <section className="relative bg-white overflow-hidden">
      <div className="grain-layer" aria-hidden="true" />
      <div className="orb top-40 -right-24 h-[300px] w-[300px] bg-[#17724F] opacity-[0.05]" aria-hidden="true" />
      <div className="relative z-10 mx-auto max-w-[900px] px-5 md:px-6 py-14 md:py-24">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#17724F]/[0.08] px-3 py-1.5">
          <AlertCircle size={14} strokeWidth={1.75} className="text-[#17724F]" aria-hidden="true" />
          <span className="text-[11.5px] font-semibold tracking-[0.14em] uppercase text-[#17724F]">
            Se você chegou até aqui
          </span>
        </div>

        <h2 className="mt-5 font-display font-normal text-[26px] sm:text-[30px] md:text-[44px] leading-[1.1] md:leading-[1.08] text-balance text-[#0B1220] max-w-[760px]">
          Se você já tentou tratamento antes,{" "}
          <span className="italic font-light gradient-text-emerald">provavelmente reconhece isso</span>
        </h2>

        <ul className="mt-10 flex flex-col gap-3">
          {items.map((text, i) => (
            <RevealItem key={i} text={text} delayMs={i * 70} index={i} />
          ))}
        </ul>

        <div className="mt-12 relative rounded-2xl bg-gradient-to-br from-[#0F2A47] via-[#0C2340] to-[#081A2E] text-white  p-6 md:p-10 overflow-hidden">
          <div className="grain-layer" aria-hidden="true" />
          <div className="absolute -top-16 -right-16 h-52 w-52 rounded-full bg-[#17724F]/35 blur-3xl" aria-hidden="true" />
          <div className="absolute -bottom-16 -left-16 h-44 w-44 rounded-full bg-[#C9A24C]/20 blur-3xl" aria-hidden="true" />
          {/* Barra dourada */}
          <div className="absolute top-0 left-0 h-1 w-24 bg-gradient-to-r from-[#2EA47A] via-[#17724F] to-[#C9A24C]" aria-hidden="true" />
          <p className="relative font-display text-[22px] sm:text-[26px] md:text-[34px] leading-[1.15] md:leading-[1.12] text-white text-balance">
            Não é falta de tratamento.{" "}
            <span className="italic text-[#C9A24C]">É falta de acompanhamento.</span>
          </p>
        </div>

        <CTAClosure />
      </div>
    </section>
  );
}

import { CTAButton } from "@/components/CTAButton";

function CTAClosure() {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={
        "reveal flex flex-col items-center text-center mt-10 md:mt-12 " +
        (visible ? "reveal-visible " : "")
      }
      style={{ transitionDelay: "420ms" }}
    >
      <p className="max-w-[520px] text-[15.5px] md:text-[17px] text-[#4A4A4E] leading-[1.55]">
        Se você se reconheceu em mais de um desses pontos, o próximo passo é simples.
      </p>
      <div className="mt-5 md:mt-6">
        <CTAButton eventName="form_open_identificacao" size="xl" />
      </div>
      <p className="mt-3 text-[12.5px] text-[#6B6B70]">
        Atendimento particular a partir de R$ 600 por consulta.
      </p>
    </div>
  );
}
